"use client";

import Link from "next/link";
import { BlueskyPost } from "../../components/BlueskyPost";
import type { DbBlueskyPost } from "../../types/cms";
import type { BlueskyPost as BlueskyPostType } from "../../types/bluesky";
import { SITE_URL } from "@/lib/site";

function generateStructuredData(post: DbBlueskyPost, slug: string) {
  const title = post.content.text.slice(0, 60).replace(/\n/g, ' ').trim();
  const description = post.content.text.slice(0, 160).replace(/\n/g, ' ').trim();
  
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "author": {
      "@type": "Person",
      "name": "Kasper Marx Andersen",
      "url": SITE_URL,
      "jobTitle": "Designer",
      "sameAs": [
        "https://www.linkedin.com/in/kaandk/",
        "https://bsky.app/profile/kasper.website"
      ]
    },
    "url": `${SITE_URL}/feed/${slug}`,
    "datePublished": post.indexed_at,
    "dateModified": post.indexed_at,
    "image": post.embed_data?.images?.[0]?.fullsize || post.embed_data?.external?.thumb || "/socialshare.png",
    "publisher": {
      "@type": "Person",
      "name": "Kasper Marx Andersen"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/feed/${slug}`
    },
    "keywords": post.hashtags.join(", "),
    "articleSection": "Design",
    "inLanguage": "en-US"
  };
}

interface FeedPostClientProps {
  post: DbBlueskyPost;
  slug: string;
}

/**
 * Convert database post to the format expected by BlueskyPost component
 */
function convertDbPostToApiFormat(dbPost: DbBlueskyPost): BlueskyPostType {
  return {
    uri: dbPost.uri,
    cid: dbPost.cid,
    author: dbPost.author_data,
    record: dbPost.content,
    embed: dbPost.embed_data,
    indexedAt: dbPost.indexed_at,
    replyCount: 0,
    repostCount: 0,
    likeCount: 0
  };
}

export function FeedPostClient({ post, slug }: FeedPostClientProps) {
  const apiPost = convertDbPostToApiFormat(post);
  const structuredData = generateStructuredData(post, slug);

  return (
    <div className="min-h-screen text-foreground">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <main className="mt-[100px] px-[20px] py-8 pb-[100px] sm:mt-[50px] sm:ml-[200px] sm:px-0 sm:pt-0">
        <div className="w-full max-w-3xl sm:pl-[50px] sm:pr-4">
          <div className="mb-8">
            <Link href="/" className="hover-invert inline-block text-[14px] font-mono mb-8">
              ← Back to Work in Progress
            </Link>
          </div>

          <article className="space-y-16 leading-[1.6]">
            <BlueskyPost post={apiPost} />
          </article>
        </div>
      </main>
    </div>
  );
}
